import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import { useTranslation } from 'react-i18next';
import DailyWorkMonthCalendar from '../components/DailyWorkMonthCalendar';
import FloorplanPinDisplay from '../components/FloorplanPinDisplay';
import AddCostSheetMappingModal from '../components/DailyWorkCostSheetMapping/AddCostSheetMappingModal';
import ResponsivePageSection from '../components/responsive/ResponsivePageSection';
import { useAuth } from '../context/AuthContext';
import { useNotification } from '../context/NotificationContext';
import {
  getProjects,
  getProjectCostSheetCategories,
  getDailyWorkCostSheetMappingCalendarStatus,
  getDailyWorkCostSheetMappingForDate,
  saveDailyWorkCostSheetMappings,
} from '../services/api';
import { canAccess } from '../utils/permissions';
import { formatDateEuropean, rangeStringsForViewMonth } from '../utils/dateUtils';
import { formatPersonnelName } from '../utils/personnelDisplay';
import { getApiErrorMessage } from '../utils/apiErrors';

function formatCostSheetLabel(sheet) {
  return [sheet.code, sheet.name].filter(Boolean).join(' — ');
}

function DailyWorkCostSheetMapping() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { showNotification } = useNotification();
  const canRead = canAccess(user, 'dailyWorkCostSheetMapping.read');
  const canWrite = canAccess(user, 'dailyWorkCostSheetMapping.write');

  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState('');
  const [date, setDate] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  });
  const [viewMonth, setViewMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [dateEntryStatus, setDateEntryStatus] = useState({});
  const [dateDetail, setDateDetail] = useState(null);
  const [draftMappings, setDraftMappings] = useState({});
  const [costSheetCategories, setCostSheetCategories] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [loadingCalendar, setLoadingCalendar] = useState(false);
  const [loadingDate, setLoadingDate] = useState(false);
  const [saving, setSaving] = useState(false);
  const [modalTask, setModalTask] = useState(null);

  useEffect(() => {
    getProjects()
      .then((res) => setProjects(res.data || []))
      .catch(() => showNotification(t('common.loadError', { defaultValue: 'Failed to load data' }), 'error'))
      .finally(() => setLoadingProjects(false));
  }, [showNotification, t]);

  const refreshCalendarMonthStatus = useCallback(async () => {
    if (!projectId || !viewMonth) return;
    setLoadingCalendar(true);
    try {
      const { fromStr, toStr } = rangeStringsForViewMonth(viewMonth);
      const res = await getDailyWorkCostSheetMappingCalendarStatus(projectId, {
        from: fromStr,
        to: toStr,
      });
      setDateEntryStatus(res.data?.dates || {});
    } catch (err) {
      showNotification(await getApiErrorMessage(err, 'Failed to load calendar status'), 'error');
    } finally {
      setLoadingCalendar(false);
    }
  }, [projectId, viewMonth, showNotification]);

  const loadDateDetail = useCallback(async () => {
    if (!projectId || !date) return;
    setLoadingDate(true);
    try {
      const res = await getDailyWorkCostSheetMappingForDate(projectId, date);
      const data = res.data;
      setDateDetail(data);
      const nextDraft = {};
      (data.tasks || []).forEach((task) => {
        nextDraft[task.id] = (task.mappings || []).map((m) => ({
          costSheetId: m.costSheetId,
          costSheet: m.costSheet,
        }));
      });
      setDraftMappings(nextDraft);
    } catch (err) {
      setDateDetail(null);
      setDraftMappings({});
      showNotification(await getApiErrorMessage(err, 'Failed to load date details'), 'error');
    } finally {
      setLoadingDate(false);
    }
  }, [projectId, date, showNotification]);

  useEffect(() => {
    refreshCalendarMonthStatus();
  }, [refreshCalendarMonthStatus]);

  useEffect(() => {
    if (projectId) {
      getProjectCostSheetCategories(projectId)
        .then((res) => setCostSheetCategories(res.data || []))
        .catch(() => setCostSheetCategories([]));
    } else {
      setCostSheetCategories([]);
    }
  }, [projectId]);

  useEffect(() => {
    if (projectId && date) {
      loadDateDetail();
    } else {
      setDateDetail(null);
      setDraftMappings({});
    }
  }, [projectId, date, loadDateDetail]);

  const handleViewMonthChange = (delta) => {
    const [y, m] = viewMonth.split('-').map(Number);
    const next = new Date(y, m - 1 + delta, 1);
    setViewMonth(`${next.getFullYear()}-${String(next.getMonth() + 1).padStart(2, '0')}`);
  };

  const allTasksMapped = useMemo(() => {
    if (!dateDetail?.tasks?.length) return false;
    return dateDetail.tasks.every((task) => (draftMappings[task.id] || []).length > 0);
  }, [dateDetail, draftMappings]);

  const handleAddMapping = (taskId, costSheetId) => {
    const task = dateDetail?.tasks?.find((item) => item.id === taskId);
    const fromLinked = task?.projectLinkedCostSheets?.find((s) => s.id === costSheetId);
    const fromTree = findCostSheetInTree(costSheetCategories, costSheetId);
    const costSheet = fromLinked || fromTree;
    if (!costSheet) return;

    setDraftMappings((prev) => {
      const current = prev[taskId] || [];
      if (current.some((m) => m.costSheetId === costSheetId)) return prev;
      return {
        ...prev,
        [taskId]: [...current, { costSheetId, costSheet }],
      };
    });
  };

  const handleRemoveMapping = (taskId, costSheetId) => {
    setDraftMappings((prev) => ({
      ...prev,
      [taskId]: (prev[taskId] || []).filter((m) => m.costSheetId !== costSheetId),
    }));
  };

  const handleSave = async () => {
    if (!projectId || !date || !allTasksMapped) return;
    setSaving(true);
    try {
      const mappings = (dateDetail.tasks || []).map((task) => ({
        projectGeneralTaskId: task.id,
        costSheetIds: (draftMappings[task.id] || []).map((m) => m.costSheetId),
      }));
      await saveDailyWorkCostSheetMappings(projectId, { date, mappings });
      showNotification(t('dailyWorkCostSheetMapping.saveSuccess'), 'success');
      await Promise.all([refreshCalendarMonthStatus(), loadDateDetail()]);
    } catch (err) {
      showNotification(await getApiErrorMessage(err, 'Failed to save mapping'), 'error');
    } finally {
      setSaving(false);
    }
  };

  if (!canRead) {
    return (
      <ResponsivePageSection>
        <Typography variant="h4" gutterBottom>{t('dailyWorkCostSheetMapping.title')}</Typography>
        <Alert severity="warning">{t('common.noPermission', { defaultValue: 'You do not have permission to view this page.' })}</Alert>
      </ResponsivePageSection>
    );
  }

  return (
    <ResponsivePageSection>
      <Typography variant="h4" gutterBottom>{t('dailyWorkCostSheetMapping.title')}</Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="mapping-project-label">{t('dailyWorkCostSheetMapping.selectProject')}</InputLabel>
            <Select
              labelId="mapping-project-label"
              value={projectId}
              label={t('dailyWorkCostSheetMapping.selectProject')}
              onChange={(e) => setProjectId(e.target.value)}
              disabled={loadingProjects}
            >
              {projects.map((p) => (
                <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {projectId ? (
            <Box sx={{ position: 'relative' }}>
              {loadingCalendar && (
                <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1, bgcolor: 'rgba(255,255,255,0.6)' }}>
                  <CircularProgress size={24} />
                </Box>
              )}
              <DailyWorkMonthCalendar
                date={date}
                viewMonth={viewMonth}
                onSelectDate={setDate}
                onViewMonthChange={handleViewMonthChange}
                dateEntryStatus={dateEntryStatus}
                mode="mapping"
                legendKey="dailyWorkCostSheetMapping.calendarDotsExplanation"
              />
            </Box>
          ) : (
            <Alert severity="info">{t('dailyWorkCostSheetMapping.selectProject')}</Alert>
          )}
        </Grid>

        <Grid item xs={12} md={8}>
          {!projectId ? null : loadingDate ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : !dateDetail?.tasks?.length ? (
            <Alert severity="info">{t('dailyWorkCostSheetMapping.noTasksForDate')}</Alert>
          ) : (
            <Box>
              <Typography variant="h6" sx={{ mb: 2 }}>
                {t('dailyWorkCostSheetMapping.selectDate')}: {formatDateEuropean(date)}
              </Typography>

              {dateDetail.tasks.map((task) => (
                <Card key={task.id} variant="outlined" sx={{ mb: 2 }}>
                  <CardContent>
                    <Typography variant="subtitle1" sx={{ mb: 1.5, fontWeight: 600 }}>
                      {task.name}
                    </Typography>

                    {task.people?.length > 0 && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                          {t('dailyWorkCostSheetMapping.people')}
                        </Typography>
                        {task.people.map((person) => (
                          <Typography key={person.id} variant="body2">
                            {person.personnel
                              ? formatPersonnelName(person.personnel)
                              : person.subcontractorCompany?.name || t('dailyWorkInput.subcontractorCompany', { defaultValue: 'Subcontractor company' })}
                            {' — '}
                            {person.manDays} {t('dailyWorkInput.step2.manDays', { defaultValue: 'Man-days' })}
                          </Typography>
                        ))}
                      </Box>
                    )}

                    {task.materials?.length > 0 && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                          {t('dailyWorkCostSheetMapping.materials')}
                        </Typography>
                        {task.materials.map((material) => (
                          <Typography key={material.id} variant="body2">
                            {material.materialEquipment?.name
                              || material.subcontractorEquipmentName
                              || t('dailyWorkInput.step2.unknownMaterial', { defaultValue: 'Unknown Material' })}
                            {material.amount != null ? ` — ${material.amount}` : ''}
                          </Typography>
                        ))}
                      </Box>
                    )}

                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                        {t('dailyWorkCostSheetMapping.pinsAndPhotos')}
                      </Typography>
                      {task.taskWorkInput?.pinGroups?.length > 0 ? (
                        task.taskWorkInput.pinGroups.map((group, groupIdx) => (
                          <Box key={group.groupKey || groupIdx} sx={{ mb: 2 }}>
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                              {group.floorplanOriginalName || t('dailyWorkInput.step3.floorplanN', { defaultValue: 'Floorplan' })}
                            </Typography>
                            <FloorplanPinDisplay
                              projectId={projectId}
                              fileId={group.floorplanFileId}
                              mimeType={group.floorplanMimeType}
                              pins={group.pins || []}
                            />
                            {(task.taskWorkInput.photos || []).length > 0 && (
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                                {(task.taskWorkInput.photos || []).map((photo) => (
                                  <Box
                                    key={photo.id}
                                    component="a"
                                    href={photo.url}
                                    target="_blank"
                                    rel="noreferrer"
                                    sx={{
                                      border: '1px solid',
                                      borderColor: 'divider',
                                      borderRadius: 1,
                                      overflow: 'hidden',
                                      width: 96,
                                      textDecoration: 'none',
                                    }}
                                  >
                                    <Box
                                      component="img"
                                      src={photo.url}
                                      alt={photo.originalName}
                                      sx={{ width: 96, height: 72, objectFit: 'cover', display: 'block' }}
                                    />
                                  </Box>
                                ))}
                              </Box>
                            )}
                          </Box>
                        ))
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          {t('dailyWorkCostSheetMapping.noPinsOrPhotos')}
                        </Typography>
                      )}
                      {task.taskWorkInput?.notes && (
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          {task.taskWorkInput.notes}
                        </Typography>
                      )}
                    </Box>

                    <Box sx={{ borderTop: 1, borderColor: 'divider', pt: 2 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        {t('dailyWorkCostSheetMapping.connectSection')}
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                        {(draftMappings[task.id] || []).map((mapping) => (
                          <Chip
                            key={mapping.costSheetId}
                            label={formatCostSheetLabel(mapping.costSheet)}
                            onDelete={canWrite ? () => handleRemoveMapping(task.id, mapping.costSheetId) : undefined}
                            deleteIcon={<DeleteIcon />}
                          />
                        ))}
                      </Box>
                      {canWrite && (
                        <Button
                          size="small"
                          startIcon={<AddIcon />}
                          onClick={() => setModalTask(task)}
                        >
                          {t('dailyWorkCostSheetMapping.addCostSheet')}
                        </Button>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              ))}

              {canWrite && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
                  <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={handleSave}
                    disabled={!allTasksMapped || saving}
                  >
                    {t('dailyWorkCostSheetMapping.saveMapping')}
                  </Button>
                  {!allTasksMapped && (
                    <Typography variant="body2" color="text.secondary">
                      {t('dailyWorkCostSheetMapping.mappingRequired')}
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          )}
        </Grid>
      </Grid>

      {modalTask && (
        <AddCostSheetMappingModal
          open={Boolean(modalTask)}
          onClose={() => setModalTask(null)}
          projectLinkedCostSheets={modalTask.projectLinkedCostSheets || []}
          costSheetCategories={costSheetCategories}
          existingMappingIds={(draftMappings[modalTask.id] || []).map((m) => m.costSheetId)}
          onConfirm={(costSheetId) => handleAddMapping(modalTask.id, costSheetId)}
        />
      )}
    </ResponsivePageSection>
  );
}

function findCostSheetInTree(categories, costSheetId) {
  for (const category of categories || []) {
    const sheet = (category.costSheets || []).find((item) => item.id === costSheetId);
    if (sheet) return sheet;
    const nested = findCostSheetInTree(category.children, costSheetId);
    if (nested) return nested;
  }
  return null;
}

export default DailyWorkCostSheetMapping;
